import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { CourseEditTime } from "@shared/models/analytics/course"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { Course, CourseCalendarDateDefault, CourseCalendarTime } from "@shared/models/course"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import type { PatchModel } from "@shared/models/patch"
import Patch from "@shared/models/patch-updater"
import type { PreparedAction } from "../../../action-doc-builder"
import { isCourseDateDefault } from "cc-frontend/utils/filter-dates/course"
import { filter, flatten, map, partial } from "lodash"
import type { Narrative } from "../../create-narrative"
import defaultCourseActivity from "../default-course-activity"
import defaultCourseContext from "../default-course-context"
import defaultCourseModels from "../default-course-models"
import COURSE_SET_ONE_TIME from "./COURSE_SET_ONE_TIME"

interface Payload {
  course: Course
  planbookId: string
  time: CourseCalendarTime | null
  rotationCalendar: RotationCalendar
}

export function prepareCourseSetAllTimesAction(payload: Payload): PreparedAction {
  return {
    name: "COURSE_SET_ALL_TIMES",
    payload: payload,
    patches: patches(payload),
    undoPatches: null,
    narrative: partial(narrative, payload),
  }
}

function patches(payload: Payload): Array<PatchModel> {
  // check if there are date defaults
  let defaultDates = filter(payload.course.attributes.calendar.dates, isCourseDateDefault)
  let patches: Array<PatchModel> = []
  if (defaultDates.length === 0) {
    let semester = payload.rotationCalendar.attributes.semesters[0]
    patches = map(payload.rotationCalendar.attributes.rotations, (rotation) => {
      return Patch.create("course", payload.course.id)
        .push("attributes.calendar.dates", {
          id: rotation.id,
          type: "course-date-default",
          attributes: {
            rotationId: rotation.id,
            semesterId: semester.id,
            cardStackId: null,
            time: payload.time,
          },
        })
        .inc("attributes.calendar.version", 1).patch
    })
  } else {
    patches = flatten(
      map(
        filter(
          payload.course.attributes.calendar.dates,
          (date) => date.type === "course-date-default"
        ),
        (courseDate: CourseCalendarDateDefault): Array<PatchModel> => {
          return COURSE_SET_ONE_TIME.patches({
            course: payload.course,
            planbookId: payload.planbookId,
            courseDate,
            // Not used by patches()
            rotationTitle: "",
            time: payload.time,
          })
        }
      )
    )
  }

  return patches
}

function narrative(payload: Payload, findFn: FindFn, userId): Narrative {
  let models = defaultCourseModels(payload, findFn, userId)
  let newEvent: CourseEditTime = {
    ...createBlankEvent(),
    root_id: payload.planbookId,
    root_type: "planbook",
    date_plan_scheduled_for: null,

    primary_verb: "edit",
    primary_possessive: getPossessiveForPlanbook(findFn, userId, payload.planbookId),

    primary_object_type: "course",
    primary_object_id: payload.course.id,
    primary_object_specifier: "time",
    primary_object_id_and_ancestor_ids: [payload.planbookId, payload.course.id],

    adverbial_verb: "editing",

    adverbial_object_type: "rotation",
    adverbial_object_id: payload.rotationCalendar.id,
    adverbial_object_specifier: "all",
    adverbial_object_id_and_ancestor_ids: [payload.planbookId, payload.course.id],
  }

  return {
    title: "Assigned Time",
    titleForAnalytics: "Course - Assigned Time to All Days",
    description: `Set the time of your ${payload.course.attributes.title} class`,
    context: defaultCourseContext(payload, models),
    activity: defaultCourseActivity(payload, models),
    newAnalyticsEvents: [newEvent],
  }
}
